import React from "react"
import { graphql, Link } from "gatsby"
import {
  FaReact,
  FaAngular,
  FaNodeJs,
  FaPython,
  FaJsSquare,
  FaHtml5,
  FaCss3,
  FaSass,
  FaBootstrap,
} from "react-icons/fa"
import {
  SiRedux,
  SiCsharp,
  SiAntdesign,
  SiMicrosoftsqlserver,
  SiPostgresql,
  SiMysql,
  SiSqlite,
  SiAmazonaws,
  SiMicrosoftazure,
  SiDocker,
  SiNginx,
  SiApache,
  SiLinux,
} from "react-icons/si"
import { GrGraphQl } from "react-icons/gr"

import Layout from "../../components/Layout"
import ServiceProof from "../../components/ServiceProof"
import ServiceTechStack from "../../components/ServiceTechStack"
import { getOffer } from "../../data/offers"

import aiChatgpt from "../../images/techstack/icons8-chatgpt-100.png"
import aiClaude from "../../images/techstack/icons8-claude-ai-100.png"
import aiTensorflow from "../../images/techstack/icons8-tensorflow-100.png"
import aiPytorch from "../../images/techstack/icons8-pytorch-100.png"
import aiLangchain from "../../images/techstack/langchain.png"
import aiElevenlabs from "../../images/techstack/icons8-elevenlabs-100.png"

import "../../styles/servicePage.scss"

/**
 * /services/webdevelopment/ — the web practice.
 *
 * Rebuilt to the /services/aisolution/ template: nine sections in the same
 * order, the same devices, the same .sp- system. The one structural addition is
 * the technology stack, which the brief required kept and placed after section
 * four — see components/ServiceTechStack.js for why it is a rebuilt section
 * rather than the old component moved.
 *
 * INDEXED. Frozen and byte-identical to git HEAD: the <h1>, the <title>, the
 * meta description, the canonical and the URL. Everything else is rewritten.
 *
 * ONE h1. The previous page rendered several, because antd's Typography.Title
 * defaults to h1 and the hero, "We Can Help With", the process heading and the
 * stack heading all used it. Only the frozen hero heading is an h1 now.
 *
 * NO WEEK NUMBERS, NO DATES, NO FIXED-TIMELINE CLAIM. The chevron process
 * diagram that used to sit here is gone: it published a five-step delivery
 * sequence written in the third person about the team rather than the reader,
 * and section 5 replaces it with named phases. The same chevron still renders on
 * /services/mobiledevelopment/ and /services/staffaugmentation/, which are not
 * in scope yet — the shared components it uses are untouched.
 *
 * Prices appear in ONE place: the offer rows in section 6.
 */

const OFFER_SLUGS = [
  "practice-and-telehealth-platform",
  "online-course-platform",
  "odoo-for-commerce-and-payments",
]

// Resolved from the catalogue so a price, a timeline or a slug can never drift
// out of sync with the offer page it points at.
const PRACTICE_OFFERS = OFFER_SLUGS.map(getOffer).filter(Boolean)

/* 2. The three audiences the brief named — private practices and clinics,
   training providers and course publishers, and online retailers selling direct
   and to trade — each get two of these, in the words they use themselves. */
const PROBLEMS = [
  "Patients ring during clinic hours to book, so your front desk spends its day on the phone instead of on the people in front of it.",
  "Learners enrol by email and somebody retypes them into a spreadsheet, which is also where you look when one of them asks about their certificate.",
  "Your storefront says one thing about stock and your accounts say another, and you find out which is right when a customer complains.",
  "Every reminder, receipt and certificate goes out because a person remembered to send it.",
  "Your site tells people about you and then asks them to email you, which is where the trail goes cold.",
  "Nobody can say how many enquiries came in last month or what happened to them without opening four different tools.",
]

/* 4. What the practice actually builds. Each card names its stack, so the terms
   sit in the copy rather than only in an icon. */
const CAPABILITIES = [
  {
    title: "Portals your customers serve themselves from",
    body:
      "A logged-in area where the people you deal with do the things they currently ring or email you about: book, rebook, pay, download, update their own details, look at their own history. The work moves off your team without moving away from you.",
    stack: ["React", "Node.js", "PostgreSQL", "Authentication", "Role-based access"],
  },
  {
    title: "Booking and scheduling that runs itself",
    body:
      "Real-time availability, instant confirmation and automatic reminders, with the rules of your business built in — notice periods, session lengths, who can see which slots. Double-bookings stop being possible rather than becoming rarer.",
    stack: ["Real-time", "Calendars", "Notifications", "Payments"],
  },
  {
    title: "Storefronts that agree with your stock and your accounts",
    body:
      "A commerce front end for direct-to-consumer and business-to-business buyers alike, with real-time inventory and personalised product recommendations, wired to the system that holds the truth so the number on the product page is the number in the warehouse. Checkout is judged on conversion rather than on how it looks.",
    stack: ["E-commerce", "D2C", "B2B", "Checkout", "Real-time inventory", "Payments", "Odoo"],
  },
  {
    title: "Integration with the systems that hold your records",
    body:
      "Most of what a web application needs already exists somewhere — in your enterprise resource planning system, your customer relationship management tool, your payment provider. This is the plumbing between those data sources: backend interfaces, event-driven messaging and scheduled syncs, so one change lands everywhere instead of in one place.",
    stack: ["API", "ERP", "CRM", "Backend", "Data sources", "Event-driven", "GraphQL"],
  },
  {
    title: "Interfaces people finish what they started in",
    body:
      "Drop-off is measurable, and it is almost always a specific screen rather than a general mood. Your frontend gets rebuilt against that behavioural data on a design system, and tuned for Core Web Vitals — the loading and stability measures search engines rank on — so pages are fast on a phone on a bad connection.",
    stack: ["UI/UX", "Frontend", "Design system", "Core Web Vitals", "SEO", "Accessibility"],
  },
  {
    title: "Intelligence where it earns its place",
    body:
      "Machine learning and AI belong in the few spots where a prediction changes what someone does: which enquiry to call first, which learner is about to lapse, which order looks wrong. Trained on your history, measured on your data, and left out where a rule would do.",
    stack: ["Python", "Machine learning", "AI", "Analytics", "Data pipelines"],
  },
]

/* 5. Named phases, no numbers and no dates. The chevron this replaces was
   written about the team — "the team reviews the product backlog" — and carried
   a fixed five-step sequence. A phase is a shape of activity, not a slot. */
const PHASES = [
  {
    phase: "Before anything is built",
    title: "What you have, and what it is costing",
    body:
      "You get an honest read on the systems you already run and what a web application would have to talk to. Where the current process leaks time is measured rather than assumed, because that is the number the work has to beat.",
  },
  {
    phase: "Agreeing the shape",
    title: "Scope written down, then held",
    body:
      "Scope is agreed in writing before work starts, and change is handled explicitly: anything new is quoted as an addition rather than absorbed quietly, so the thing you approved stays the thing being built.",
  },
  {
    phase: "While it is being built",
    title: "Something to open every cycle",
    body:
      "Work is pulled from a backlog you can see and lands in short sprints on an environment you can log into. Progress is a screen you click through, not a status report — early enough that steering it is still cheap.",
  },
  {
    phase: "Before it goes near your customers",
    title: "Tested, then released without a held breath",
    body:
      "Automated testing runs on every change, so a regression is caught before it reaches production rather than by whoever notices it first — which is what turns QA from a queue at the end into a check that runs all the way through. Cloud deployment is rehearsed rather than attempted, so going live is an ordinary afternoon with no downtime.",
  },
  {
    phase: "After it is live",
    title: "Watched, maintained, and yours",
    body:
      "Monitoring and alerting sit on the running system, and maintenance keeps dependencies current instead of letting them rot. Your code, your data and your infrastructure are yours — you can take them elsewhere.",
  },
]

/* 7. Proof from this discipline only. Nurtureful Psychology is a web
   application build — portal, booking, telehealth, billing — and its case study
   route was verified live rather than inferred from a filename.

   THE FIGURES ARE THE CLIENT'S AND ARE QUOTED, NOT REWRITTEN. */
const PROOF = {
  client: "Nurtureful Psychology",
  path: "/caseStudies/projects/nurturefulPsychology",
  metrics: [
    "50% reduction in administrative time spent on scheduling and manual patient onboarding",
    "35% decrease in session no-shows through automated multi-channel appointment reminders",
    // The numeral is gone, deliberately. The case study carries this as
    // metric "100%" (nurturefulPsychology.js:42), but 100% is not a measurable
    // property of security and HIPAA alignment is not a percentage — and the
    // service page had already dropped the client's own qualifier ("ensuring
    // complete patient confidentiality"), so the numeral was attaching directly
    // to the word "secure". "all" goes with it. ServiceProof renders a metric
    // with no leading figure below the stat row rather than inventing one.
    "HIPAA-aligned digital workflow across online touchpoints",
  ],
}

/* 8. Questions about the discipline, not about any one offer. */
const FAQS = [
  {
    q: "We already have a website. Why would we need this as well?",
    a: "Usually you do not need it as well — the site becomes the front of it. A website is where people find out about you; the part being described here is where they do something. If everything a visitor wants to do still ends in an email to your team, that is the gap, and it is the only reason to build.",
  },
  {
    q: "Do we have to replace the systems we already run?",
    a: "No, and it is usually the wrong move. The records you keep in an enterprise resource planning or customer relationship management system stay where they are; this connects to them through the interfaces they already expose. Replacing a working system is a much larger project and a different conversation.",
  },
  {
    q: "Who owns the code?",
    a: "You do, along with the data and the infrastructure it runs on. Repositories, environments and documentation are handed over as a matter of course, not as a concession at the end. You can take the whole thing to another team without asking anyone's permission.",
  },
  {
    q: "How long does this kind of work take?",
    a: "Open-ended work is scoped to your situation, which is why no duration is published for it here. Three builds in this practice have been delivered often enough to state their scope and duration upfront, and those are listed above — if a date you can hold someone to matters more than an exact fit, start there.",
  },
  {
    q: "What stops it slowing down once real people are using it?",
    a: "The architecture is chosen for the load you expect rather than the load you have: cloud-native and event-driven where that earns its keep, with the database engine picked for your query patterns. Performance is measured on real devices and real networks, and monitoring tells you it is drifting before your customers do.",
  },
  {
    q: "What does it cost?",
    a: "Work without a published scope is quoted against the scope once it is written, so there is no useful figure to print here. What gets agreed alongside it is how you will judge whether it paid — in hours returned or errors removed, in your numbers rather than ours.",
  },
]

/* 4b. The technology stack, kept in full. Both the icon and its name render, so
   these are terms in the copy rather than pictures a reader has to recognise. */
const STACK_GROUPS = [
  {
    label: "Languages and frameworks",
    items: [
      { node: <FaReact />, name: "React JS" },
      { node: <FaAngular />, name: "Angular" },
      { node: <FaNodeJs />, name: "Node.js" },
      { node: <FaPython />, name: "Python" },
      { node: <SiCsharp />, name: "C#" },
      { node: <GrGraphQl />, name: "GraphQL" },
      { node: <FaJsSquare />, name: "JavaScript" },
      { node: <SiRedux />, name: "Redux" },
      { node: <FaHtml5 />, name: "HTML5" },
      { node: <FaCss3 />, name: "CSS3" },
      { node: <FaSass />, name: "Sass" },
      { node: <FaBootstrap />, name: "Bootstrap" },
      { node: <SiAntdesign />, name: "Ant Design" },
    ],
  },
  {
    label: "Databases",
    items: [
      { node: <SiMicrosoftsqlserver />, name: "Microsoft SQL Server" },
      { node: <SiPostgresql />, name: "PostgreSQL" },
      { node: <SiMysql />, name: "MySQL" },
      { node: <SiSqlite />, name: "SQLite" },
    ],
  },
  {
    label: "Infrastructure",
    items: [
      { node: <SiAmazonaws />, name: "AWS" },
      { node: <SiMicrosoftazure />, name: "Microsoft Azure" },
      { node: <SiDocker />, name: "Docker" },
      { node: <SiNginx />, name: "Nginx" },
      { node: <SiApache />, name: "Apache" },
      { node: <SiLinux />, name: "Linux" },
    ],
  },
  {
    label: "AI and machine learning",
    items: [
      { node: <img src={aiChatgpt} alt="" />, name: "ChatGPT" , chip: true },
      { node: <img src={aiClaude} alt="" />, name: "Claude" , chip: true },
      { node: <img src={aiTensorflow} alt="" />, name: "TensorFlow" , chip: true },
      { node: <img src={aiPytorch} alt="" />, name: "PyTorch" , chip: true },
      { node: <img src={aiLangchain} alt="" />, name: "LangChain" , chip: true },
      { node: <img src={aiElevenlabs} alt="" />, name: "ElevenLabs" , chip: true },
    ],
  },
]

/* Rewritten to second person. The previous three paragraphs opened "We build",
   "Our database architecture" and "We deploy". */
const STACK_PROSE = [
  "Nothing here is chosen because it is fashionable. Your application is built on languages and frameworks with a long support horizon and a large enough pool of engineers that you are never dependent on one person — React JS, Angular, Node.js, Python, C# and GraphQL — and the frontend pieces that let a page load fast on a phone are treated as part of the build rather than a later optimisation.",
  "Your database engine is chosen against your data, not by habit. Microsoft SQL Server, PostgreSQL, MySQL and SQLite each suit a different shape of problem, and which one you get depends on how much you hold, how you query it, and whether any machine learning sits downstream of it.",
  "Where it runs is chosen the same way. AWS, Microsoft Azure, Docker, Nginx, Apache and Linux give you a cloud-native foundation you can scale and, just as importantly, one you could move away from — and the AI and machine learning tools alongside them, ChatGPT, Claude, TensorFlow, PyTorch, LangChain and ElevenLabs, are used where a model genuinely helps rather than everywhere at once.",
]

const WebDevelopment = ({ data }) => (
  <Layout
    className="services-page-container"
    data={data}
    // FROZEN — byte-identical to git HEAD. Do not touch.
    pageTitle={"Visiomate - Web Development Services"}
    descrip={
      "From design to deployment, we handle every aspect of web development. Partner with us for innovative, scalable, and engaging web solutions."
    }
  >
    <article className="sp">
      {/* ── 1. HERO — FULL WIDTH, no split, no rail ─────────────────────────── */}
      <section className="sp-hero">
        <div className="sp-hero__inner">
          <span className="sp-tag">Web practice</span>
          {/* FROZEN h1. The only h1 on the page. */}
          <h1 className="sp-hero__h1">
            AI-Powered Web Applications Built for Enterprise Scale
          </h1>
          <p className="sp-hero__lede">
            You already have customers, records and a team that knows the work.
            What you do not have is a place your customers can do things for
            themselves, wired to the systems where the answers already live.
            That is the gap this practice closes — web applications built around
            your workflows and your records rather than a template you bend
            yourself around.
          </p>
          <Link to="/contact" className="sp-hero__cta">
            Talk through what you are dealing with
          </Link>
        </div>
      </section>

      {/* ── 2. PROBLEMS — two columns, hairlines, no numbers, no cards ──────── */}
      <section className="sp-problems">
        <div className="sp-problems__inner">
          <span className="sp-tag">Where this starts</span>
          <h2 className="sp-h2">The problems this practice solves</h2>
          <p className="sp-lede">
            None of these are web problems to begin with. They are handover
            problems, re-keying problems and where-is-it problems, and they are
            what people are describing when they ask for a new site.
          </p>
          <ul className="sp-problems__grid">
            {PROBLEMS.map(item => (
              <li className="sp-problems__item" key={item}>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── 3. WHAT THIS ACTUALLY IS — plain language, no bare acronyms ─────── */}
      <section className="sp-what">
        <div className="sp-what__inner">
          {/* FIX 1 — two columns. This ran 300+ words in one narrow left
              column with the right half of the page empty. The heading and a
              standfirst carry the point on the left; the argument is broken
              into its own divisions on the right, at a 34rem measure. */}
          <div className="sp-what__cols">
            <div className="sp-what__lead">
              <span className="sp-tag">In plain terms</span>
              <h2 className="sp-h2">What this actually is</h2>
              <p className="sp-what__standfirst">
                Three words do most of the confusing here: application,
                integrated, and portal. The distance between what each is
                assumed to mean and what it actually means is where most of your
                team's day goes.
              </p>
            </div>
            <div className="sp-what__body">
            <div className="sp-what__part">
              <h3 className="sp-what__parttitle">A website versus a web application</h3>
              <p>
                A website is something you read. A web application is something you use.
                The difference is not how it looks — both open in a browser, and a good
                one of either loads fast and reads well on a phone. The difference is
                that a website tells your customer something and a web application lets
                them do something and remembers that they did: book the appointment, pay
                the invoice, enrol on the course, check what they ordered last time.
              </p>
            </div>
            <div className="sp-what__part">
              <h3 className="sp-what__parttitle">What “integrated” actually means</h3>
              <p>
                Which brings up the word that gets used most and explained least.
                &ldquo;Integrated&rdquo; does not mean two systems sitting on the same
                screen. It means that when something changes in one, the other knows,
                without a person in the middle. A booking made at eleven at night
                appears in the calendar your staff open at eight, the payment lands
                against the right record in your accounts, and nobody retypes anything.
                Where that link is missing, the person in the middle is your team, and
                the cost of it is a day at a time.
              </p>
            </div>
            <div className="sp-what__part">
              <h3 className="sp-what__parttitle">Why a portal beats email and spreadsheets</h3>
              <p>
                This is also the honest answer to why a portal beats email and
                spreadsheets. Email and a spreadsheet genuinely work — right up to the
                point where you need to know something. They hold information but they
                cannot answer questions, so the moment you ask how many, or which ones
                are outstanding, or what happened to that one, the only way to find out
                is for a person to go and look. A portal is the same information in a
                place that can answer, available to your customer at midnight and to you
                as a number rather than a search.
              </p>
            </div>
            </div>
          </div>
          <p className="sp-what__pull">
            The useful question is never whether you need a new website. It is which
            thing your customers currently have to ask a human for, which they would
            happily do themselves at two in the morning.
          </p>
        </div>
      </section>

      {/* ── 4. WHAT GETS BUILT WITH IT — THE DARK BAND, cards ───────────────── */}
      <section className="sp-build">
        <div className="sp-build__inner">
          <span className="sp-tag">The capabilities</span>
          <h2 className="sp-h2">What gets built with it</h2>
          <p className="sp-lede">
            Six things account for nearly every engagement in this practice.
            Most projects are two or three of them wired together, not all six.
          </p>
          <div className="sp-build__grid">
            {CAPABILITIES.map(cap => (
              <div className="sp-build__card" key={cap.title}>
                <h3 className="sp-build__cardtitle">{cap.title}</h3>
                <p className="sp-build__cardbody">{cap.body}</p>
                <ul className="sp-build__stack">
                  {cap.stack.map(tech => (
                    <li className="sp-build__stackitem" key={tech}>
                      {tech}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4b. TECHNOLOGY STACK — kept, moved here, rewritten to second person */}
      <ServiceTechStack
        eyebrow="What it is built on"
        heading="The technology stack behind it"
        lede="You inherit these choices for years, so they are worth seeing before you commit rather than discovering afterwards."
        groups={STACK_GROUPS}
        paragraphs={STACK_PROSE}
      />

      {/* ── 5. HOW WE WORK — named phases, no week numbers, no dates ────────── */}
      <section className="sp-how">
        <div className="sp-how__inner">
          <span className="sp-tag">How the work runs</span>
          <h2 className="sp-h2">How open-ended work is run</h2>
          <p className="sp-lede">
            Work in this practice rarely arrives with a date attached, so what
            you get instead of a delivery date is visibility: agreed scope,
            something to open every cycle, and a way out.
          </p>
          <ol className="sp-how__steps">
            {PHASES.map(ph => (
              <li className="sp-how__step" key={ph.title}>
                <span className="sp-how__marker" aria-hidden="true" />
                <span className="sp-how__phase">{ph.phase}</span>
                <h3 className="sp-how__title">{ph.title}</h3>
                <p className="sp-how__body">{ph.body}</p>
              </li>
            ))}
          </ol>
          <p className="sp-how__note">
            No date is published for work of this shape, and none is implied. If
            what you need is a scope and a date you can hold someone to, the
            packages below carry both.
          </p>
        </div>
      </section>

      {/* ── 6. FIXED-SCOPE OPTIONS — rows, prices shown ─────────────────────── */}
      <section className="sp-offers">
        <div className="sp-offers__inner">
          <span className="sp-tag">Fixed-scope options</span>
          <h2 className="sp-h2">Some of this work has a written scope already</h2>
          <p className="sp-lede">
            Three builds in this practice have been delivered enough times to
            state upfront what they contain, how long they take and what they
            cost.
          </p>
          <ul className="sp-offers__list">
            {PRACTICE_OFFERS.map(o => (
              <li className="sp-offers__item" key={o.slug}>
                <Link to={`/offers/${o.slug}`} className="sp-offers__row">
                  <span className="sp-offers__main">
                    <span className="sp-offers__name">{o.name}</span>
                    <span className="sp-offers__problem">{o.symptoms[0]}</span>
                  </span>
                  <span className="sp-offers__meta">
                    <span className="sp-offers__timeline">
                      {o.timelineShort}
                    </span>
                    <span className="sp-offers__price">{o.priceShort}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <p className="sp-offers__all">
            <Link to="/offers">Every offer, across every practice &rarr;</Link>
          </p>
        </div>
      </section>

      {/* ── 7. PROOF — the shared component. FIX 2: every metric leads
             with its figure at $fs-stat, a CLIENT tag carries the attribution,
             and the name links to the case study because that route resolves.
             components/ServiceProof.js ─────────────────────────────────────── */}
      <ServiceProof
        client={PROOF.client}
        path={PROOF.path}
        metrics={PROOF.metrics}
      />

      {/* ── 8. QUESTIONS — about the discipline ─────────────────────────────── */}
      <section className="sp-faq">
        <div className="sp-faq__inner">
          <span className="sp-tag">Questions</span>
          <h2 className="sp-h2">What buyers ask about web work</h2>
          <ul className="sp-faq__list">
            {FAQS.map(f => (
              <li className="sp-faq__item" key={f.q}>
                <p className="sp-faq__q">{f.q}</p>
                <p className="sp-faq__a">{f.a}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── 9. IF YOUR NEED IS BROADER — one CTA ────────────────────────────── */}
      <section className="sp-broader">
        <div className="sp-broader__inner">
          <span className="sp-tag">If your need is broader</span>
          <h2 className="sp-h2">If none of that is quite your situation</h2>
          <div className="sp-broader__cols">
            <p className="sp-broader__text">
              Plenty of what lands in this practice does not fit a package: an
              outdated portal held together with manual workarounds that still
              has to run while its replacement is built, a legacy storefront
              that has outgrown the platform it started on and needs a rewrite
              rather than a patch, an interface that has to satisfy an auditor,
              or engineers working alongside your own team on a roadmap you
              already have.
            </p>
            <p className="sp-broader__text">
              Describe what you are dealing with in a free consultancy session
              and you will get a straight read on how it would be approached,
              what it depends on, and where the risk sits. If the answer is that
              a smaller change would do, that is what you will hear.
            </p>
          </div>
          <div className="sp-broader__foot">
            <Link to="/contact" className="sp-broader__cta">
              Tell us what you are dealing with
            </Link>
          </div>
        </div>
      </section>
    </article>
  </Layout>
)

export default WebDevelopment

export const webDevelopmentQuery = graphql`
  query webDevelopmentQuery {
    bgImage: file(relativePath: { eq: "Mask_group8.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
  }
`
